import React, { useState } from 'react';
import { upperFirst } from 'lodash';
import { Badge } from './ui/badge';
import { Button } from '@dashboard/components/ui/button';
import Dialog from './Dialog';
import { formatDateTime } from '@/dashboard/lib/format-date';
import Dash from './Dash';
import EmailRecipients from './EmailRecipients';

export const GUEST_REMINDER_LOG_TYPE = 'attendee_details_reminder';

function OpenedStatus({ opened, openedAt }) {
  if (opened === true && openedAt) {
    return `${formatDateTime(openedAt)}`;
  }

  if (opened === true) {
    return <Badge variant="success">Opened</Badge>;
  } else if (opened === false) {
    return <Badge variant="info">Unopened</Badge>;
  } else {
    return <Dash />;
  }
}

function DeliveredStatus({ delivered, deliveredAt }) {
  if (delivered === true && deliveredAt) {
    return `${formatDateTime(deliveredAt)}`;
  }

  if (delivered === true) {
    return <Badge variant="success">Delivered</Badge>;
  } else if (delivered === false) {
    return <Badge variant="destructive">Pending</Badge>;
  } else {
    return <Dash />;
  }
}

export const getDisplayStatusInfo = log => {
  const event = log.postmarkMessageEvent;

  if (event === 'Delivered' || event === 'Opened') {
    return { text: 'Delivered', variant: 'success' };
  }

  if (event === 'Bounced') {
    return { text: 'Bounced', variant: 'destructive' };
  }

  if (event === 'Queued') {
    return { text: 'Processing', variant: 'warning' };
  }

  switch (log.status) {
    case 'created':
      return { text: 'Created', variant: 'info' };
    case 'pending':
      return { text: 'Processing', variant: 'warning' };
    case 'succeeded':
      return { text: 'Sent', variant: 'success' };
    case 'failed':
      return { text: 'Failed', variant: 'destructive' };
    default:
      return { text: log.status || 'Unknown', variant: 'info' };
  }
};

const formattedLogType = logType => {
  if (logType === 'manual_email') {
    return 'Manual email';
  }
  if (logType === GUEST_REMINDER_LOG_TYPE) {
    return 'Attendee reminder';
  }
  if (logType === 'attendee_ticket') {
    return 'Attendee ticket';
  }
  if (
    ['abandonment_reminder_1', 'abandonment_reminder_2', 'abandonment_reminder_3'].includes(logType)
  ) {
    return upperFirst(logType.replace('abandonment_', '').replace('_', ' '));
  } else {
    return upperFirst(
      logType
        .replace('charge_', '')
        .replace('subscription_', '')
        .replace('submission_', '')
        .replace('payment_', ''),
    );
  }
};

function ViewEmailAction({ log }) {
  const [showEmailContent, setShowEmailContent] = useState(false);
  const emailBody = `data:text/html;charset=utf-8,${encodeURIComponent(log.body)}`;

  if (!log.body) return null;

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setShowEmailContent(true)}>
        View email
      </Button>

      <Dialog
        open={showEmailContent}
        onCancel={() => setShowEmailContent(false)}
        size="2xl"
        cancelButton={null}
        confirmButton={null}
      >
        <div className="mb-4">
          <div className="text-sm text-gray-600 dark:text-muted-foreground">
            Subject: {log.subject}
          </div>
          <div className="text-sm text-gray-600 dark:text-muted-foreground">From: {log.from}</div>
          <div className="text-sm text-gray-600 dark:text-muted-foreground">
            To:{' '}
            <EmailRecipients recipients={log.recipients} recipientEvents={log.recipientEvents} />
          </div>
          {log.attachments && log.attachments.length > 0 && (
            <div className="text-sm text-gray-600 dark:text-muted-foreground">
              Attachments:{' '}
              {log.attachments.map((attachment, i) => (
                <React.Fragment key={attachment.name}>
                  <a
                    href={`data:application/pdf;base64,${attachment.content}`}
                    download={attachment.name}
                    className="underline text-black dark:text-foreground"
                  >
                    {attachment.name}
                  </a>
                  {i < log.attachments.length - 1 && ', '}
                </React.Fragment>
              ))}
            </div>
          )}
          <div className="text-sm text-gray-600 dark:text-muted-foreground">
            As sent at: {formatDateTime(log.createdAt)}
          </div>
        </div>

        <div className="w-full h-[70vh] border border-gray-100 dark:border-border rounded-lg">
          <iframe className="w-full h-[70vh]" src={emailBody} />
        </div>
      </Dialog>
    </>
  );
}

// A bounced email has a message id: it did go out. Without one, Postmark never accepted it.
const neverSent = log => ['created', 'failed'].includes(log.status) && !log.postmarkMessageId;

export function getEmailLogColumns() {
  return [
    {
      accessorKey: 'recipients',
      header: 'Recipient',
      cell: ({ row }) => (
        <EmailRecipients
          recipients={row.original.recipients}
          recipientEvents={row.original.recipientEvents}
          layout="table"
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'type',
      header: 'Email type',
      cell: ({ row }) => <Badge variant="info">{formattedLogType(row.original.type)}</Badge>,
      enableSorting: false,
    },
    {
      accessorKey: 'createdAt',
      header: 'Sent',
      cell: ({ row }) =>
        row.original.createdAt && !neverSent(row.original) ? (
          <span className="whitespace-nowrap">{formatDateTime(row.original.createdAt)}</span>
        ) : (
          <Dash />
        ),
      enableSorting: false,
    },
    {
      accessorKey: 'status',
      header: 'Send status',
      cell: ({ row }) => {
        const log = row.original;
        const { text, variant } = getDisplayStatusInfo(log);
        return log.status ? <Badge variant={variant}>{text}</Badge> : <Dash />;
      },
      enableSorting: false,
    },
    {
      accessorKey: 'delivered',
      header: 'Delivery',
      cell: ({ row }) => (
        <DeliveredStatus
          delivered={row.original.delivered}
          deliveredAt={row.original.deliveredAt}
        />
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'opened',
      header: 'Opened',
      cell: ({ row }) => (
        <OpenedStatus opened={row.original.opened} openedAt={row.original.openedAt} />
      ),
      enableSorting: false,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => <ViewEmailAction log={row.original} />,
      enableSorting: false,
      meta: { className: 'text-right' },
    },
  ];
}
